import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import {
  colors,
  spaces,
  mixins,
} from 'ech-components/src/core/styles/theme/default';
import { supportedLanguages } from '~/core/utils/i18n';
import { createLanguagePath } from '@scripts/utils/domain';

const StyledLanguageSwitcher = styled.nav(
  ({ borders }: { borders?: boolean }) => css`
    display: inline-flex;
    width: 100%;

    ${borders &&
    css`
      padding: ${rem(spaces.epsilon)} ${rem(spaces.beta)};
      border-top: solid ${rem(1)} ${colors.white};
      border-bottom: solid ${rem(1)} ${colors.white};
    `}
  `,
);

const StyledLanguageLink = styled.a(
  ({ color }: { color?: string }) => css`
    padding: ${rem(5)};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${colors.white};
    font-weight: 400;
    margin-right: ${rem(8)};
    font-size: ${rem(16)};
    line-height: ${rem(20)};

    ${mixins.MQ('large')} {
      font-size: ${rem(18)};
      line-height: ${rem(28)};
      margin-right: ${rem(14)};
    }

    ${color &&
    css`
      color: ${color};
    `}

    &:hover,
    &:focus {
      color: ${colors.secondarySoftPurple200};
    }
  `,
);

const LanguageSwitcher = ({
  langObj,
  borders,
  color,
  className,
}: LanguageSwitcherProps) => {
  return (
    <StyledLanguageSwitcher
      className={className}
      borders={borders ? true : false}
    >
      {supportedLanguages.map(({ languageTag }: { languageTag: string }) => (
        <StyledLanguageLink
          key={languageTag}
          color={color}
          href={
            langObj
              ? createLanguagePath({
                  switcher: langObj.switcher,
                  language: languageTag,
                })
              : `/${languageTag}`
          }
        >
          {languageTag.toUpperCase()}
        </StyledLanguageLink>
      ))}
    </StyledLanguageSwitcher>
  );
};

export type LanguageSwitcherProps = {
  /** Variant Name of the LanguageSwitcher - possible values: **emphasized** */
  variant?: 'horizontal' | 'vertical';
  /** Class of the LanguageSwitcher */
  className?: string;
  /** context from gatsby-node.js with grouped pages and actual folder */
  langObj: any;
  /** Show top and bottom borders */
  borders?: Boolean;
  /** Links color */
  color?: string;
};

export default LanguageSwitcher;
